import { Icon20ChevronUp } from '@vkontakte/icons';
import cn from 'clsx';
import React, {
    type PropsWithChildren,
    type ReactNode,
    Children,
    cloneElement,
    isValidElement,
    useCallback,
    useEffect,
    useRef,
    useState,
} from 'react';
import { useClickOutsideTarget } from 'reactApp/hooks/useClickOutsideTarget';
import { noop } from 'reactApp/utils/helpers';

import styles from './ButtonWithSelect.css';

interface ButtonWithSelectProps {
    buttonValue?: string;
    disabled: boolean;
    sendGa: (action: string, label: string) => void;
    children: JSX.Element | Array<JSX.Element>;
}

function ButtonWithSelect({ buttonValue, disabled, children, sendGa }: ButtonWithSelectProps) {
    const [isOpen, setIsOpen] = useState(false);
    const wrapperRef = useRef<HTMLDivElement>(null);

    const handleMenuOnClick = useCallback(() => {
        setIsOpen(false);
    }, []);

    const handleExpandOnClick = useCallback(() => {
        setIsOpen((isOpen) => !isOpen);
    }, []);

    useClickOutsideTarget({
        ref: wrapperRef,
        onClickAway: () => {
            setIsOpen(false);
        },
    });

    const ButtonComponent = Children.map(children, (child: ReactNode) => {
        if (!isValidElement(child) || child.type !== Option) {
            return null;
        }
        if (!buttonValue || child.props.id === buttonValue) {
            return cloneElement(child, {
                ...child.props,
                className: cn(styles.actionButton, child.props.className),
                onClick: (...args) => {
                    sendGa('action-item-clicked', '');
                    child.props?.onClick(...args);
                },
                icon: null,
                children: child.props.actionTitle,
            });
        }
        return null;
    }).find((c) => c !== null);

    return (
        <div ref={wrapperRef} className={cn(styles.wrapper, { [styles.disabled]: disabled })}>
            <div className={cn(styles.actionButtonContainer, { [styles.actionButtonBorder]: Boolean(ButtonComponent) })}>
                {ButtonComponent ?? <div />}

                {ButtonComponent ? (
                    <button className={cn(styles.expandButton)} onClick={handleExpandOnClick} data-qa-id="expand-button">
                        <Icon20ChevronUp className={cn(styles.expandIcon, { [styles.rotate]: !isOpen })} />
                    </button>
                ) : null}
            </div>

            {isOpen && <SelectOptionsList onClick={handleMenuOnClick}>{children}</SelectOptionsList>}
        </div>
    );
}

type SelectOptionsProps = PropsWithChildren<{
    onClick: () => void;
}>;

function SelectOptionsList({ onClick, children }: SelectOptionsProps) {
    return (
        <div className={styles.list} onClick={onClick}>
            {children}
        </div>
    );
}

interface OptionProps {
    children: ReactNode;
    id: string;
    actionTitle: string;
    icon: ReactNode;
    sendGa: (action: string, label: string) => void;
    className?: string;
    onClick?: (id: any, cb: any, e?: any) => void;
}

function Option({ id, onClick, className, icon, sendGa, children }: OptionProps) {
    const handleClicked = useCallback(
        (e: React.MouseEvent<HTMLDivElement>) => {
            onClick?.(id, noop, e);
        },
        [id, onClick]
    );

    useEffect(() => {
        sendGa(`item-show`, id);
    }, [id, sendGa]);

    return (
        <div className={cn(className, styles.item)} onClick={handleClicked} data-name={id}>
            {isValidElement(icon) ? <div className={cn(styles.itemIcon, icon.props.className)}>{icon}</div> : null}
            <div className={styles.itemText}>{children}</div>
        </div>
    );
}

Option.displayName = 'ButtonWithSelect.Option';

function Title({ children }) {
    return <div className={styles.title}>{children}</div>;
}

Title.displayName = 'ButtonWithSelect.Title';

export default Object.assign(ButtonWithSelect, { Option, Title });
